// src/views/pages/dynamic-price/services/dynamicPriceApi.js
// ─────────────────────────────────────────────────────────────
// Dynamic Pricing API Service
// Base: /dynamic-price/<route>
// Auth: Bearer token from localStorage
// ─────────────────────────────────────────────────────────────

import axios from "axios";

const apiUrl = String(process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const base = `${apiUrl}/dynamic-price`;

// ── Helpers ──────────────────────────────────────────────────
const getHeaders = () => {
  const token = localStorage.getItem("token") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const withAuth = (extra = {}) => ({
  headers: getHeaders(),
  ...extra,
});

/**
 * Optional helper you can use in pages:
 * const companyId = getCompanyId();
 */
export const getCompanyId = () => {
  const raw = localStorage.getItem("companyid");
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 1;
};

// ── Dashboard ────────────────────────────────────────────────
export const getDashboardStats = (companyid) =>
  axios.get(`${base}/dashboard`, withAuth({ params: { companyid } }));

export const runAnalysis = (companyid) =>
  axios.post(`${base}/analyse`, { companyid }, withAuth());

// Optional future endpoint (if backend adds it)
// export const getOccupancyTrend = (companyid) =>
//   axios.get(`${base}/dashboard/occupancy-trend`, withAuth({ params: { companyid } }));

// ── Recommendations ──────────────────────────────────────────
/**
 * filters can include:
 * - status
 * - source_channel
 * - checkin_date
 * - from_date / to_date
 * - room_type_id / room_type_name
 * (backend-dependent)
 */
export const getRecommendations = (companyid, filters = {}) =>
  axios.get(
    `${base}/recommendations`,
    withAuth({ params: { companyid, ...filters } })
  );

export const approveRecommendation = (companyid, id) =>
  axios.patch(`${base}/recommendations/${id}/approve`, { companyid }, withAuth());

export const rejectRecommendation = (companyid, id) =>
  axios.patch(`${base}/recommendations/${id}/reject`, { companyid }, withAuth());

export const modifyRecommendation = (companyid, id, final_price) =>
  axios.patch(
    `${base}/recommendations/${id}/modify`,
    { companyid, final_price: Number(final_price) },
    withAuth()
  );

// Migration fix: PATCH (not POST)
export const approveAllRecommendations = (companyid, payload = {}) =>
  axios.patch(
    `${base}/recommendations/approve-all`,
    { companyid, ...payload },
    withAuth()
  );

// Optional if backend supports granular source approval for grouped UI
export const approveRecommendationBySource = (companyid, id, source_channel) =>
  axios.patch(
    `${base}/recommendations/${id}/approve`,
    { companyid, source_channel },
    withAuth()
  );

export const rejectRecommendationBySource = (companyid, id, source_channel) =>
  axios.patch(
    `${base}/recommendations/${id}/reject`,
    { companyid, source_channel },
    withAuth()
  );

export const modifyRecommendationBySource = (
  companyid,
  id,
  source_channel,
  final_price
) =>
  axios.patch(
    `${base}/recommendations/${id}/modify`,
    { companyid, source_channel, final_price: Number(final_price) },
    withAuth()
  );

// ── Competitors ──────────────────────────────────────────────
export const getCompetitors = (companyid) =>
  axios.get(`${base}/competitors`, withAuth({ params: { companyid } }));

/**
 * data shape (example)
 * {
 *   name,
 *   platform,
 *   city,
 *   url
 * }
 */
export const addCompetitor = (companyid, data) =>
  axios.post(`${base}/competitors`, { companyid, ...data }, withAuth());

export const deleteCompetitor = (companyid, id) =>
  axios.delete(`${base}/competitors/${id}`, withAuth({ params: { companyid } }));

export const scrapeCompetitor = (companyid, id) =>
  axios.post(`${base}/competitors/${id}/scrape`, { companyid }, withAuth());

export const scrapeAllCompetitors = (companyid) =>
  axios.post(`${base}/competitors/scrape-all`, { companyid }, withAuth());

// Added helper for enable/disable competitor tracking
export const toggleCompetitor = (companyid, id) =>
  axios.patch(`${base}/competitors/${id}/toggle`, { companyid }, withAuth());

// ── Reports ──────────────────────────────────────────────────
/**
 * month format example: "2026-02"
 */
export const getMonthlyReport = (companyid, month) =>
  axios.get(`${base}/reports/monthly`, withAuth({ params: { companyid, month } }));

// Optional future reports endpoints (keep commented until backend exists)
// export const getDailyReport = (companyid, date) =>
//   axios.get(`${base}/reports/daily`, withAuth({ params: { companyid, date } }));

// ── Integration ──────────────────────────────────────────────
export const getIntegrationStatus = (companyid) =>
  axios.get(`${base}/integration/status`, withAuth({ params: { companyid } }));

/**
 * mode can be:
 * - "PMS"        (Own PMS / Native)
 * - "INTEGRATION" (3rd-party PMS)
 * - "MANUAL"
 *
 * config can include:
 * - live_method ("webhook" | "daily")
 * - fallback_policy
 * - manual_occupancy_pct
 * - etc.
 */
export const saveIntegrationMode = (companyid, mode, config = {}) =>
  axios.put(
    `${base}/integration/mode`,
    { companyid, mode, ...config },
    withAuth()
  );

export const testIntegrationConnection = (companyid) =>
  axios.post(`${base}/integration/test`, { companyid }, withAuth());

// External room types pushed by 3rd-party PMS vendor via /setup
export const getExternalRoomTypes = (companyid) =>
  axios.get(
    `${base}/integration/external-room-types`,
    withAuth({ params: { companyid } })
  );

// mappings = [{ external_id, our_room_type_id | null, ignored?: boolean }]
export const saveRoomMapping = (companyid, mappings) =>
  axios.post(
    `${base}/integration/room-mapping`,
    { companyid, mappings },
    withAuth()
  );

export const getSavedRoomMapping = (companyid) =>
  axios.get(
    `${base}/integration/room-mapping`,
    withAuth({ params: { companyid } })
  );

// External rate plans pushed by 3rd-party PMS vendor via /setup
export const getExternalRatePlans = (companyid) =>
  axios.get(
    `${base}/integration/external-rate-plans`,
    withAuth({ params: { companyid } })
  );

// mappings = [{ code, channel, commission_pct }]
export const saveRatePlanMapping = (companyid, mappings) =>
  axios.post(
    `${base}/integration/rate-plan-mapping`,
    { companyid, mappings },
    withAuth()
  );

export const getSavedRatePlanMapping = (companyid) =>
  axios.get(
    `${base}/integration/rate-plan-mapping`,
    withAuth({ params: { companyid } })
  );

// Migration fix: /integration/bulk-sync
export const triggerBulkSync = (companyid, from_date, to_date) =>
  axios.post(
    `${base}/integration/bulk-sync`,
    { companyid, from_date, to_date },
    withAuth()
  );

// Optional: fetch sync history / logs if backend provides it
export const getIntegrationSyncHistory = (companyid, params = {}) =>
  axios.get(
    `${base}/integration/sync-history`,
    withAuth({ params: { companyid, ...params } })
  );

// Optional: save webhook/fallback policy explicitly (if backend exposes endpoint)
export const saveIntegrationGoLiveConfig = (companyid, payload = {}) =>
  axios.put(
    `${base}/integration/go-live`,
    { companyid, ...payload },
    withAuth()
  );

// ── Settings ─────────────────────────────────────────────────
export const getSettings = (companyid) =>
  axios.get(`${base}/settings`, withAuth({ params: { companyid } }));

export const saveSettings = (companyid, settings) =>
  axios.put(`${base}/settings`, { companyid, ...settings }, withAuth());

// Added helper (migration guide)
export const getCronHistory = (companyid) =>
  axios.get(`${base}/settings/cron-history`, withAuth({ params: { companyid } }));

// ── Utility exports (optional) ───────────────────────────────
export const __dynamicPriceApiMeta = {
  apiUrl,
  base,
};

// ── Seasons ──────────────────────────────────────────────────
export const getSeasons = (companyid) =>
  axios.get(`${base}/settings/seasons`, withAuth({ params: { companyid } }));

export const saveSeasons = (companyid, seasons) =>
  axios.put(`${base}/settings/seasons`, { companyid, seasons }, withAuth());

// ── Events ───────────────────────────────────────────────────
export const getEvents = (companyid) =>
  axios.get(`${base}/settings/events`, withAuth({ params: { companyid } }));

export const saveEvents = (companyid, events) =>
  axios.put(`${base}/settings/events`, { companyid, events }, withAuth());